import React, { useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useResetUserPassword,
  User,
  UserRole,
  UserStatus,
  CreateUserInput,
  UpdateUserInput,
  roleLabels,
  statusLabels,
  roleColors,
  statusColors,
} from '../hooks/useUsers';

type ModalType = 'create' | 'edit' | 'toggleStatus' | 'resetPassword' | null;

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { users, loading, refetch: _refetch } = useUsers();
  const { createUser, creating } = useCreateUser();
  const { updateUser, updating } = useUpdateUser();
  const { resetPassword, resetting } = useResetUserPassword();
  const [togglingStatus, setTogglingStatus] = useState(false);

  // State
  const [modalType, setModalType] = useState<ModalType>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = useState<UserStatus | 'ALL'>('ALL');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'VIEWER' as UserRole,
    status: 'ACTIVE' as UserStatus,
    accesoConfiguracion: false,
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const matchesSearch =
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesRole = filterRole === 'ALL' || user.role === filterRole;
      const matchesStatus = filterStatus === 'ALL' || user.status === filterStatus;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, filterRole, filterStatus]);

  // Handlers
  const openCreateModal = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: 'VIEWER',
      status: 'ACTIVE',
      accesoConfiguracion: false,
    });
    setError('');
    setSuccess('');
    setModalType('create');
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      confirmPassword: '',
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      status: user.status,
      accesoConfiguracion: user.accesoConfiguracion || false,
    });
    setError('');
    setSuccess('');
    setModalType('edit');
  };

  const openToggleStatusModal = (user: User) => {
    setSelectedUser(user);
    setError('');
    setModalType('toggleStatus');
  };

  const openResetPasswordModal = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setError('');
    setModalType('resetPassword');
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedUser(null);
    setError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.username.trim() || !formData.email.trim() || !formData.password ||
        !formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Todos los campos son requeridos');
      return;
    }
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const input: CreateUserInput = {
      username: formData.username.trim(),
      email: formData.email.trim(),
      password: formData.password,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      role: formData.role,
      createdBy: currentUser?.id,
    };

    const result = await createUser(input);
    if (result?.success) {
      setSuccess(`Usuario ${formData.username} creado exitosamente`);
      setTimeout(() => {
        closeModal();
        setSuccess('');
      }, 1500);
    } else {
      setError(result?.message || 'Error al crear usuario');
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setError('');

    const input: UpdateUserInput = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      role: formData.role,
      status: formData.status,
      accesoConfiguracion: formData.accesoConfiguracion,
      updatedBy: currentUser?.id,
    };

    const result = await updateUser(selectedUser.id, input);
    if (result?.success) {
      setSuccess('Usuario actualizado exitosamente');
      setTimeout(() => {
        closeModal();
        setSuccess('');
      }, 1500);
    } else {
      setError(result?.message || 'Error al actualizar usuario');
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    setError('');
    setTogglingStatus(true);

    const newStatus: UserStatus = selectedUser.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';

    const result = await updateUser(selectedUser.id, {
      status: newStatus,
      updatedBy: currentUser?.id,
    });

    setTogglingStatus(false);

    if (result?.success) {
      setSuccess(`Usuario ${newStatus === 'ACTIVE' ? 'habilitado' : 'inhabilitado'} exitosamente`);
      setTimeout(() => {
        closeModal();
        setSuccess('');
      }, 1500);
    } else {
      setError(result?.message || 'Error al cambiar estado del usuario');
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    setError('');

    if (newPassword.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    const result = await resetPassword(selectedUser.id, newPassword, currentUser?.id || 'admin');
    if (result?.success) {
      setSuccess('Contraseña reseteada exitosamente');
      setTimeout(() => {
        closeModal();
        setSuccess('');
      }, 1500);
    } else {
      setError(result?.message || 'Error al resetear contraseña');
    }
  };

  const formatDate = (date: Date | null | string) => {
    if (!date) return 'Nunca';
    const d = new Date(date);
    return d.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="ump-container">
      <style>{`
        .ump-container {
          padding: 1.5rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Header */
        .ump-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .ump-header-info h1 {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--text-primary, #111827);
          margin: 0 0 0.25rem 0;
        }

        .ump-header-info p {
          color: var(--text-secondary, #6b7280);
          margin: 0;
          font-size: 0.9rem;
        }

        .ump-stats {
          display: flex;
          gap: 1rem;
        }

        .ump-stat-card {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          padding: 1rem 1.5rem;
          border-radius: 12px;
          text-align: center;
          min-width: 100px;
        }

        .ump-stat-card.active {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
        }

        .ump-stat-number {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .ump-stat-label {
          font-size: 0.75rem;
          opacity: 0.9;
          text-transform: uppercase;
        }

        /* Toolbar */
        .ump-toolbar {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .ump-search {
          flex: 1;
          min-width: 250px;
          position: relative;
        }

        .ump-search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-secondary, #9ca3af);
          width: 18px;
          height: 18px;
        }

        .ump-search input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.75rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 10px;
          font-size: 0.9rem;
          background: var(--input-bg, #fff);
          color: var(--text-primary, #111827);
          transition: all 0.2s;
        }

        .ump-search input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .ump-filter-group {
          display: flex;
          gap: 0.75rem;
        }

        .ump-filter-select {
          padding: 0.75rem 1rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 10px;
          font-size: 0.9rem;
          background: var(--input-bg, #fff);
          color: var(--text-primary, #111827);
          cursor: pointer;
          min-width: 140px;
        }

        .ump-filter-select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .ump-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.25rem;
          border-radius: 10px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }

        .ump-btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
        }

        .ump-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .ump-btn-secondary {
          background: var(--bg-secondary, #f3f4f6);
          color: var(--text-primary, #374151);
          border: 2px solid var(--border-color, #e5e7eb);
        }

        .ump-btn-secondary:hover {
          background: var(--border-color, #e5e7eb);
        }

        .ump-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
        }

        .ump-btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .ump-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .ump-btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .ump-btn-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          padding: 0;
          border-radius: 8px;
          background: var(--bg-secondary, #f3f4f6);
          border: 1px solid var(--border-color, #e5e7eb);
          cursor: pointer;
          color: var(--text-secondary, #6b7280);
          transition: all 0.2s ease;
        }

        .ump-btn-icon svg {
          width: 16px;
          height: 16px;
          flex-shrink: 0;
        }

        .ump-btn-icon:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .ump-btn-icon.ump-action-edit {
          color: #3b82f6;
          background: rgba(59, 130, 246, 0.1);
          border-color: rgba(59, 130, 246, 0.2);
        }

        .ump-btn-icon.ump-action-edit:hover {
          background: rgba(59, 130, 246, 0.2);
          border-color: #3b82f6;
        }

        .ump-btn-icon.ump-action-key {
          color: #f59e0b;
          background: rgba(245, 158, 11, 0.1);
          border-color: rgba(245, 158, 11, 0.2);
        }

        .ump-btn-icon.ump-action-key:hover {
          background: rgba(245, 158, 11, 0.2);
          border-color: #f59e0b;
        }

        .ump-btn-icon.ump-action-disable {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
          border-color: rgba(239, 68, 68, 0.2);
        }

        .ump-btn-icon.ump-action-disable:hover {
          background: rgba(239, 68, 68, 0.2);
          border-color: #ef4444;
        }

        .ump-btn-icon.ump-action-enable {
          color: #10b981;
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.2);
        }

        .ump-btn-icon.ump-action-enable:hover {
          background: rgba(16, 185, 129, 0.2);
          border-color: #10b981;
        }

        .ump-btn svg {
          width: 18px;
          height: 18px;
        }

        /* Table */
        .ump-table-container {
          background: var(--card-bg, #fff);
          border-radius: 16px;
          border: 1px solid var(--border-color, #e5e7eb);
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .ump-table {
          width: 100%;
          border-collapse: collapse;
        }

        .ump-table th {
          background: var(--bg-secondary, #f9fafb);
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.8rem;
          text-transform: uppercase;
          color: var(--text-secondary, #6b7280);
          border-bottom: 2px solid var(--border-color, #e5e7eb);
        }

        .ump-table td {
          padding: 1rem;
          border-bottom: 1px solid var(--border-color, #f3f4f6);
          font-size: 0.9rem;
          color: var(--text-primary, #374151);
        }

        .ump-table tbody tr {
          transition: background 0.15s;
        }

        .ump-table tbody tr:hover {
          background: var(--bg-hover, #f9fafb);
        }

        .ump-user-cell {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .ump-avatar {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          color: white;
          font-size: 0.9rem;
        }

        .ump-user-info {
          display: flex;
          flex-direction: column;
        }

        .ump-user-name {
          font-weight: 600;
          color: var(--text-primary, #111827);
        }

        .ump-user-email {
          font-size: 0.8rem;
          color: var(--text-secondary, #6b7280);
        }

        .ump-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.35rem 0.75rem;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .ump-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        .ump-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-start;
        }

        /* Loading & Empty */
        .ump-loading {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--text-secondary, #6b7280);
        }

        .ump-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-color, #e5e7eb);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .ump-empty {
          text-align: center;
          padding: 4rem 2rem;
          color: var(--text-secondary, #6b7280);
        }

        .ump-empty-icon {
          width: 60px;
          height: 60px;
          margin: 0 auto 1rem;
          opacity: 0.5;
        }

        /* Modal */
        .ump-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          backdrop-filter: blur(4px);
        }

        .ump-modal {
          background: var(--card-bg, #fff);
          border-radius: 20px;
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .ump-modal.ump-modal-wide {
          max-width: 580px;
        }

        .ump-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .ump-modal-header h2 {
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
          color: var(--text-primary, #111827);
        }

        .ump-modal-close {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: none;
          background: var(--bg-secondary, #f3f4f6);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-secondary, #6b7280);
          transition: all 0.2s;
        }

        .ump-modal-close:hover {
          background: var(--border-color, #e5e7eb);
          color: var(--text-primary, #111827);
        }

        .ump-modal-body {
          padding: 1.5rem;
        }

        .ump-modal-footer {
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-secondary, #f9fafb);
          border-radius: 0 0 20px 20px;
        }

        /* Form Sections */
        .ump-form-section {
          margin-bottom: 1.5rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .ump-form-section:last-of-type {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .ump-form-section-title {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--text-primary, #374151);
          margin: 0 0 1rem 0;
          padding-bottom: 0.5rem;
        }

        .ump-form-section-title svg {
          color: #3b82f6;
          flex-shrink: 0;
        }

        /* Form */
        .ump-form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .ump-form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .ump-form-group.full-width {
          grid-column: 1 / -1;
        }

        .ump-form-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary, #374151);
        }

        .ump-form-label .required {
          color: #ef4444;
          margin-left: 0.25rem;
        }

        .ump-form-input {
          padding: 0.75rem 1rem;
          border: 2px solid var(--border-color, #e5e7eb);
          border-radius: 10px;
          font-size: 0.9rem;
          background: var(--input-bg, #fff);
          color: var(--text-primary, #111827);
          transition: all 0.2s;
        }

        .ump-form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .ump-form-input:disabled {
          background: var(--bg-secondary, #f3f4f6);
          cursor: not-allowed;
        }

        .ump-password-wrapper {
          position: relative;
        }

        .ump-password-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--text-secondary, #9ca3af);
          padding: 0.25rem;
        }

        .ump-password-toggle:hover {
          color: var(--text-primary, #374151);
        }

        .ump-checkbox-group {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: var(--bg-secondary, #f9fafb);
          border-radius: 10px;
          border: 2px solid var(--border-color, #e5e7eb);
        }

        .ump-checkbox-group input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: #3b82f6;
        }

        .ump-checkbox-label {
          font-size: 0.9rem;
          color: var(--text-primary, #374151);
        }

        .ump-alert {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          border-radius: 10px;
          margin-top: 1rem;
          font-size: 0.9rem;
        }

        .ump-alert-error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .ump-alert-success {
          background: #f0fdf4;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .ump-alert svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        /* Toggle status confirmation */
        .ump-toggle-warning {
          text-align: center;
          padding: 1rem 0;
        }

        .ump-toggle-icon {
          width: 60px;
          height: 60px;
          margin: 0 auto 1rem;
        }

        .ump-toggle-icon.ump-toggle-disable {
          color: #ef4444;
        }

        .ump-toggle-icon.ump-toggle-enable {
          color: #10b981;
        }

        .ump-toggle-user-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary, #111827);
          margin-bottom: 0.5rem;
        }

        .ump-toggle-message {
          color: var(--text-secondary, #6b7280);
          font-size: 0.9rem;
          line-height: 1.5;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .ump-header {
            flex-direction: column;
            align-items: stretch;
          }

          .ump-stats {
            justify-content: center;
          }

          .ump-toolbar {
            flex-direction: column;
          }

          .ump-search {
            width: 100%;
          }

          .ump-filter-group {
            width: 100%;
            flex-wrap: wrap;
          }

          .ump-filter-select {
            flex: 1;
            min-width: 120px;
          }

          .ump-table-container {
            overflow-x: auto;
          }

          .ump-table {
            min-width: 700px;
          }

          .ump-form-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Dark mode */
        [data-theme="dark"] .ump-table th {
          background: #1f2937;
          color: #9ca3af;
        }

        [data-theme="dark"] .ump-table tbody tr:hover {
          background: #1f2937;
        }

        [data-theme="dark"] .ump-modal {
          background: #1f2937;
        }

        [data-theme="dark"] .ump-modal-footer {
          background: #111827;
        }
      `}</style>

      {/* Header */}
      <div className="ump-header">
        <div className="ump-header-info">
          <h1>Gestión de Usuarios</h1>
          <p>Administra los usuarios y sus permisos del sistema</p>
        </div>
        <div className="ump-stats">
          <div className="ump-stat-card">
            <div className="ump-stat-number">{users.length}</div>
            <div className="ump-stat-label">Total</div>
          </div>
          <div className="ump-stat-card active">
            <div className="ump-stat-number">{users.filter(u => u.status === 'ACTIVE').length}</div>
            <div className="ump-stat-label">Activos</div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="ump-toolbar">
        <div className="ump-search">
          <svg className="ump-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Buscar por nombre, usuario o email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="ump-filter-group">
          <select
            className="ump-filter-select"
            value={filterRole}
            onChange={e => setFilterRole(e.target.value as UserRole | 'ALL')}
          >
            <option value="ALL">Todos los roles</option>
            {Object.entries(roleLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            className="ump-filter-select"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value as UserStatus | 'ALL')}
          >
            <option value="ALL">Todos los estados</option>
            {Object.entries(statusLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <button className="ump-btn ump-btn-primary" onClick={openCreateModal}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Nuevo Usuario
        </button>
      </div>

      {/* Table */}
      <div className="ump-table-container">
        {loading ? (
          <div className="ump-loading">
            <div className="ump-spinner"/>
            <p>Cargando usuarios...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="ump-empty">
            <svg className="ump-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <p>{searchTerm || filterRole !== 'ALL' || filterStatus !== 'ALL'
              ? 'No se encontraron usuarios con los filtros aplicados'
              : 'No hay usuarios registrados'}</p>
          </div>
        ) : (
          <table className="ump-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Rol</th>
                <th>Estado</th>
                <th>Último Acceso</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>
                    <div className="ump-user-cell">
                      <div
                        className="ump-avatar"
                        style={{ background: roleColors[user.role] }}
                      >
                        {user.firstName[0]}{user.lastName[0]}
                      </div>
                      <div className="ump-user-info">
                        <span className="ump-user-name">{user.firstName} {user.lastName}</span>
                        <span className="ump-user-email">{user.email}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className="ump-badge"
                      style={{
                        background: `${roleColors[user.role]}15`,
                        color: roleColors[user.role]
                      }}
                    >
                      {roleLabels[user.role]}
                    </span>
                  </td>
                  <td>
                    <span
                      className="ump-badge"
                      style={{
                        background: `${statusColors[user.status]}15`,
                        color: statusColors[user.status]
                      }}
                    >
                      <span className="ump-badge-dot" style={{ background: statusColors[user.status] }}/>
                      {statusLabels[user.status]}
                    </span>
                  </td>
                  <td>{formatDate(user.lastLoginAt)}</td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="ump-actions">
                      <button
                        className="ump-btn-icon ump-action-edit"
                        title="Editar usuario"
                        onClick={() => openEditModal(user)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button
                        className="ump-btn-icon ump-action-key"
                        title="Resetear contraseña"
                        onClick={() => openResetPasswordModal(user)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                        </svg>
                      </button>
                      {/* No permitir que el usuario se inhabilite a sí mismo */}
                      {user.id !== currentUser?.id && (
                        <button
                          className={`ump-btn-icon ${user.status === 'ACTIVE' ? 'ump-action-disable' : 'ump-action-enable'}`}
                          title={user.status === 'ACTIVE' ? 'Inhabilitar usuario' : 'Habilitar usuario'}
                          onClick={() => openToggleStatusModal(user)}
                        >
                          {user.status === 'ACTIVE' ? (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(modalType === 'create' || modalType === 'edit') && (
        <div className="ump-modal-overlay" onClick={closeModal}>
          <div className="ump-modal ump-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="ump-modal-header">
              <h2>{modalType === 'create' ? 'Crear Nuevo Usuario' : 'Editar Usuario'}</h2>
              <button className="ump-modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <form onSubmit={modalType === 'create' ? handleCreate : handleUpdate}>
              <div className="ump-modal-body">
                {/* Sección: Información de Cuenta */}
                {modalType === 'create' && (
                  <div className="ump-form-section">
                    <h3 className="ump-form-section-title">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                      Información de Cuenta
                    </h3>
                    <div className="ump-form-grid">
                      <div className="ump-form-group">
                        <label className="ump-form-label">
                          Usuario<span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          name="username"
                          className="ump-form-input"
                          value={formData.username}
                          onChange={handleInputChange}
                          placeholder="nombre.usuario"
                          required
                        />
                      </div>
                      <div className="ump-form-group">
                        <label className="ump-form-label">
                          Email<span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className="ump-form-input"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="usuario@email.com"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Sección: Datos Personales */}
                <div className="ump-form-section">
                  <h3 className="ump-form-section-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <line x1="20" y1="8" x2="20" y2="14"/>
                      <line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                    Datos Personales
                  </h3>
                  <div className="ump-form-grid">
                    {modalType === 'edit' && (
                      <div className="ump-form-group">
                        <label className="ump-form-label">
                          Email<span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          className="ump-form-input"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="usuario@email.com"
                          required
                        />
                      </div>
                    )}
                    <div className="ump-form-group">
                      <label className="ump-form-label">
                        Nombre<span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        className="ump-form-input"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Nombre"
                        required
                      />
                    </div>
                    <div className="ump-form-group">
                      <label className="ump-form-label">
                        Apellido<span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        className="ump-form-input"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Apellido"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Sección: Contraseña (solo crear) */}
                {modalType === 'create' && (
                  <div className="ump-form-section">
                    <h3 className="ump-form-section-title">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Contraseña
                    </h3>
                    <div className="ump-form-grid">
                      <div className="ump-form-group">
                        <label className="ump-form-label">
                          Contraseña<span className="required">*</span>
                        </label>
                        <div className="ump-password-wrapper">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            className="ump-form-input"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Mín. 8 caracteres"
                            required
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <button
                            type="button"
                            className="ump-password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="ump-form-group">
                        <label className="ump-form-label">
                          Confirmar Contraseña<span className="required">*</span>
                        </label>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          className="ump-form-input"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          placeholder="Confirmar contraseña"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Sección: Rol y Permisos */}
                <div className="ump-form-section">
                  <h3 className="ump-form-section-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                    Rol y Permisos
                  </h3>
                  <div className="ump-form-grid">
                    <div className="ump-form-group">
                      <label className="ump-form-label">Rol del Usuario</label>
                      <select
                        name="role"
                        className="ump-form-input"
                        value={formData.role}
                        onChange={handleInputChange}
                      >
                        {Object.entries(roleLabels).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                    {modalType === 'edit' && (
                      <div className="ump-form-group">
                        <label className="ump-form-label">Estado</label>
                        <select
                          name="status"
                          className="ump-form-input"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          {Object.entries(statusLabels).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {modalType === 'edit' && (
                    <div className="ump-checkbox-group" style={{ marginTop: '1rem' }}>
                      <input
                        type="checkbox"
                        id="accesoConfiguracion"
                        name="accesoConfiguracion"
                        checked={formData.accesoConfiguracion}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="accesoConfiguracion" className="ump-checkbox-label">
                        Acceso a Configuración del Sistema
                      </label>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="ump-alert ump-alert-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="ump-alert ump-alert-success">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M9 12l2 2 4-4"/>
                    </svg>
                    <span>{success}</span>
                  </div>
                )}
              </div>
              <div className="ump-modal-footer">
                <button type="button" className="ump-btn ump-btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="ump-btn ump-btn-primary"
                  disabled={creating || updating}
                >
                  {(creating || updating) ? 'Guardando...' : (modalType === 'create' ? 'Crear Usuario' : 'Guardar Cambios')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toggle Status Modal */}
      {modalType === 'toggleStatus' && selectedUser && (
        <div className="ump-modal-overlay" onClick={closeModal}>
          <div className="ump-modal" onClick={e => e.stopPropagation()}>
            <div className="ump-modal-header">
              <h2>{selectedUser.status === 'ACTIVE' ? 'Inhabilitar Usuario' : 'Habilitar Usuario'}</h2>
              <button className="ump-modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className="ump-modal-body">
              <div className="ump-toggle-warning">
                {selectedUser.status === 'ACTIVE' ? (
                  <svg className="ump-toggle-icon ump-toggle-disable" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                  </svg>
                ) : (
                  <svg className="ump-toggle-icon ump-toggle-enable" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                )}
                <p className="ump-toggle-user-name">{selectedUser.firstName} {selectedUser.lastName}</p>
                <p className="ump-toggle-message">
                  {selectedUser.status === 'ACTIVE'
                    ? 'El usuario será inhabilitado y no podrá acceder al sistema hasta que se vuelva a habilitar.'
                    : 'El usuario será habilitado y podrá acceder nuevamente al sistema.'}
                </p>
              </div>
              {error && (
                <div className="ump-alert ump-alert-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                  </svg>
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="ump-alert ump-alert-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                  <span>{success}</span>
                </div>
              )}
            </div>
            <div className="ump-modal-footer">
              <button className="ump-btn ump-btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button
                className={`ump-btn ${selectedUser.status === 'ACTIVE' ? 'ump-btn-danger' : 'ump-btn-success'}`}
                onClick={handleToggleStatus}
                disabled={togglingStatus}
              >
                {togglingStatus
                  ? (selectedUser.status === 'ACTIVE' ? 'Inhabilitando...' : 'Habilitando...')
                  : (selectedUser.status === 'ACTIVE' ? 'Inhabilitar Usuario' : 'Habilitar Usuario')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {modalType === 'resetPassword' && selectedUser && (
        <div className="ump-modal-overlay" onClick={closeModal}>
          <div className="ump-modal" onClick={e => e.stopPropagation()}>
            <div className="ump-modal-header">
              <h2>Resetear Contraseña</h2>
              <button className="ump-modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="ump-modal-body">
                <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                  Establece una nueva contraseña para <strong>{selectedUser.firstName} {selectedUser.lastName}</strong>
                </p>
                <div className="ump-form-group">
                  <label className="ump-form-label">
                    Nueva Contraseña<span className="required">*</span>
                  </label>
                  <div className="ump-password-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="ump-form-input"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="Mín. 8 caracteres"
                      required
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <button
                      type="button"
                      className="ump-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="ump-alert ump-alert-error">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                {success && (
                  <div className="ump-alert ump-alert-success">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M9 12l2 2 4-4"/>
                    </svg>
                    <span>{success}</span>
                  </div>
                )}
              </div>
              <div className="ump-modal-footer">
                <button type="button" className="ump-btn ump-btn-secondary" onClick={closeModal}>
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="ump-btn ump-btn-primary"
                  disabled={resetting}
                >
                  {resetting ? 'Guardando...' : 'Cambiar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
